/**
 * ListALoan -- the LPM-2 list-a-loan wizard.
 *
 * The FI lead's surface for creating a participation offering FROM one of
 * their originated Loan records: prefilled where the loan carries a real
 * field, linked via CreateOfferingRequest.loanId, and honest everywhere
 * else. Four steps, one page at /syndication/list-a-loan (a page, not a
 * dialog: the sibling syndication surfaces are routed pages, and step 1
 * carries a loan grid that does not fit the single-form Dialog pattern the
 * IOI flow uses).
 *
 * Loan source: GET /api/admin/loans/active (platform-admin-api
 * lending-view BFF) via the existing loansApi client / useActiveLoans
 * hook. The fetch uses the 'Active' display bucket (raw statuses ACTIVE /
 * FUNDED / APPROVED); only ACTIVE and FUNDED rows are selectable -- a
 * participation sells an interest in a funded balance on the book.
 * APPROVED (nothing funded yet) renders disabled with the reason. The
 * loans read is gated server-side on enableLosPreview and 404s by design
 * when the flag is off; that failure renders an honest empty state with a
 * tracked change-request link (GH #1962 -- no dead ends).
 *
 * Prefill honesty: only fields the Loan record actually carries are
 * prefilled (principal -> loanAmount, interestRate -> coupon,
 * originationDate, termMonths, maturityDate, paymentFrequency, and a
 * dealName suggestion from purpose/loanNumber). Retention, syndication
 * amounts, minimum commitment, deadline, loan type, and the collateral
 * evaluation id are the lead's to supply -- the loan does not carry them
 * and nothing here invents a value.
 *
 * Contract note (GH #2162): CreateOfferingRequest carries an optional
 * trueSaleAttested field, and this page sends it -- the attestation is a
 * persisted fact on the offering, with the attesting actor + timestamp
 * stamped server-side (DB-only provenance) inside the create transaction.
 * The submit gate below (blocked until the lead attests) remains a product
 * choice on top of that: the SERVICE accepts an unattested create, this
 * wizard does not offer one. regulatoryFramework stays server-defaulted
 * and there is no originatorRetainedPct on the create contract -- the
 * lead's retention already rides participationStructure.leadRetainedAmount.
 *
 * Participant context: POST /offerings requires body.leadParticipantId to
 * resolve to a CrtParticipant in the caller's tenant. The caller's own
 * participant rows come from GET /api/v1/crt/network/participants (the
 * list is tenant-scoped server-side); zero rows renders the same
 * request-activation path the IOI dialog uses.
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AlertCircle, Inbox, Plus, Trash2 } from 'lucide-react';
import {
  Card,
  Button,
  Badge,
  Input,
  Select,
  Checkbox,
  Skeleton,
  EmptyState,
  DataGrid,
  FormHelperText,
} from '@aaimplatform/ui';
import type { ColumnDef } from '@aaimplatform/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { components as LspComponents } from '@aaimplatform/institutional-types/loan-syndication-protocol';
import type { AdminPortalBff } from '@aaimplatform/institutional-types';
import { loanSyndicationClient } from '../../api/client';
import { useOwnParticipants } from './own-participants';
import { useActiveLoans } from '../../hooks/useActiveLoans';
import { requestChangeHref } from '../../utils/request-change';
import { loanStatusLabel } from '../../api/loan-labels';
import { formatCurrencyGrid, formatPercent, formatDate, labelFor, LOAN_TYPE_LABELS } from './format';

type LoanViewItem = AdminPortalBff.components['schemas']['LoanViewItem'];
type CreateOfferingRequest = LspComponents['schemas']['CreateOfferingRequest'];
type ParticipationOffering = LspComponents['schemas']['ParticipationOffering'];
type LoanDetails = LspComponents['schemas']['LoanDetails'];
type AmortType = LspComponents['schemas']['AmortType'];
type PrepayModel = LspComponents['schemas']['PrepayModel'];
type NetworkVisibility = LspComponents['schemas']['NetworkVisibility'];
type AmortizationScheduleRow = LspComponents['schemas']['AmortizationScheduleRow'];
type ErrorResponse = LspComponents['schemas']['ErrorResponse'];

// ---------------------------------------------------------------------------
// Loan-status selectability (step 1)
// ---------------------------------------------------------------------------

/**
 * Raw LoanStatus values a lead can list a participation from: the loan is
 * funded and living on the book. Everything else either is not a loan yet
 * (DRAFT/SUBMITTED/UNDERWRITING), has no funded balance (APPROVED), has
 * nothing outstanding (CLOSED), or is impaired (SUSPENDED/DEFAULTED) --
 * listing an impaired loan without workout disclosure misrepresents the
 * asset, and that flow is out of LPM-2 scope.
 */
export const SYNDICABLE_LOAN_STATUSES = ['ACTIVE', 'FUNDED'] as const;

export const NON_SYNDICABLE_REASON: Record<string, string> = {
  DRAFT: 'Draft application -- not an originated loan yet.',
  SUBMITTED: 'Application submitted -- not an originated loan yet.',
  UNDERWRITING: 'In underwriting -- not an originated loan yet.',
  APPROVED: 'Approved but not yet funded -- no funded balance on the book to participate.',
  SUSPENDED: 'Suspended -- impaired loans are not listable from this wizard.',
  DEFAULTED: 'Defaulted -- impaired loans are not listable from this wizard.',
  CLOSED: 'Closed -- nothing outstanding to participate.',
};

export function isSyndicableStatus(status: string | undefined): boolean {
  return !!status && (SYNDICABLE_LOAN_STATUSES as readonly string[]).includes(status);
}

// ---------------------------------------------------------------------------
// Prefill (step 2) -- only real Loan-record fields, never invented
// ---------------------------------------------------------------------------

export interface TermsFormState {
  dealName: string;
  loanAmount: string;
  interestRate: string;
  rateType: 'FIXED' | 'VARIABLE';
  termMonths: string;
  loanType: '' | LoanDetails['loanType'];
  originationDate: string;
  maturityDate: string;
  paymentFrequency: 'MONTHLY' | 'QUARTERLY';
  servicingFeeRate: string;
  collateralEvaluationId: string;
  commitmentDeadline: string;
  totalSyndicationAmount: string;
  leadRetainedAmount: string;
  minimumCommitment: string;
  maximumCommitment: string;
  maximumParticipants: string;
  distributionWaterfall: 'PRO_RATA' | 'SEQUENTIAL';
  trueSaleAttested: boolean;
}

export interface AmortFormState {
  amortType: '' | AmortType;
  prepayModel: '' | PrepayModel;
  prepaySpeed: string;
  scheduleRows: Array<{ date: string; scheduledPrincipal: string; balance: string }>;
}

export interface VisibilityFormState {
  networkVisibility: NetworkVisibility;
  invitedTenantIds: string[];
}

export const EMPTY_TERMS: TermsFormState = {
  dealName: '',
  loanAmount: '',
  interestRate: '',
  rateType: 'FIXED',
  termMonths: '',
  loanType: '',
  originationDate: '',
  maturityDate: '',
  paymentFrequency: 'MONTHLY',
  servicingFeeRate: '',
  collateralEvaluationId: '',
  commitmentDeadline: '',
  totalSyndicationAmount: '',
  leadRetainedAmount: '',
  minimumCommitment: '',
  maximumCommitment: '',
  maximumParticipants: '',
  distributionWaterfall: 'PRO_RATA',
  trueSaleAttested: false,
};

export const EMPTY_AMORT: AmortFormState = {
  amortType: '',
  prepayModel: '',
  prepaySpeed: '',
  scheduleRows: [],
};

export const EMPTY_VISIBILITY: VisibilityFormState = {
  networkVisibility: 'NETWORK',
  invitedTenantIds: [],
};

/**
 * Derive the step-2 prefill from the picked loan. A key is present in the
 * result ONLY when the Loan record carries the field -- an absent
 * originationDate / termMonths / maturityDate / paymentFrequency yields no
 * key at all, so the form field stays empty for the lead to supply.
 * (loanType is deliberately NOT derived: the record's loan_type vocabulary
 * -- e.g. REVOLVING -- does not map onto the offering's PLEDGED_ASSET /
 * SECURITIES_BACKED / DIGITAL_ASSET_BACKED enum, and guessing from the
 * collateral label would be a fabricated mapping.)
 */
export function prefillFromLoan(loan: LoanViewItem): Partial<TermsFormState> {
  const prefill: Partial<TermsFormState> = {};
  if (typeof loan.loanAmount === 'number') {
    prefill.loanAmount = String(loan.loanAmount);
  }
  if (typeof loan.interestRate === 'number') {
    prefill.interestRate = String(loan.interestRate);
  }
  if (typeof loan.termMonths === 'number') {
    prefill.termMonths = String(loan.termMonths);
  }
  if (loan.originationDate) {
    prefill.originationDate = loan.originationDate.slice(0, 10);
  }
  if (loan.maturityDate) {
    prefill.maturityDate = loan.maturityDate.slice(0, 10);
  }
  if (loan.paymentFrequency === 'MONTHLY' || loan.paymentFrequency === 'QUARTERLY') {
    prefill.paymentFrequency = loan.paymentFrequency;
  }
  if (loan.purpose) {
    prefill.dealName = `${loan.purpose} participation`;
  } else if (loan.loanNumber) {
    prefill.dealName = `Loan ${loan.loanNumber} participation`;
  }
  return prefill;
}

// ---------------------------------------------------------------------------
// Validation -- required fields per the generated contract, plus the
// structural invariants the create service enforces (mirrored so the lead
// sees them before the round-trip; the server remains authoritative).
// ---------------------------------------------------------------------------

const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function validateTerms(terms: TermsFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  const loanAmount = Number(terms.loanAmount);
  if (!terms.loanAmount || !Number.isFinite(loanAmount) || loanAmount <= 0) {
    errors.loanAmount = 'Loan amount must be a positive number.';
  }
  const rate = Number(terms.interestRate);
  if (terms.interestRate === '' || !Number.isFinite(rate) || rate < 0) {
    errors.interestRate = 'Interest rate is required (decimal fraction, e.g. 0.065 for 6.5%).';
  }
  const term = Number(terms.termMonths);
  if (!terms.termMonths || !Number.isInteger(term) || term < 1) {
    errors.termMonths = 'Term must be a whole number of months, at least 1.';
  }
  if (!terms.loanType) {
    errors.loanType = 'Loan type is required.';
  }
  if (!terms.originationDate) {
    errors.originationDate = 'Origination date is required.';
  }
  if (!terms.collateralEvaluationId) {
    errors.collateralEvaluationId = 'Collateral evaluation id is required.';
  } else if (!UUID_SHAPE.test(terms.collateralEvaluationId)) {
    errors.collateralEvaluationId = 'Collateral evaluation id must be a UUID.';
  }
  if (!terms.commitmentDeadline) {
    errors.commitmentDeadline = 'Commitment deadline is required.';
  }
  const total = Number(terms.totalSyndicationAmount);
  if (!terms.totalSyndicationAmount || !Number.isFinite(total) || total <= 0) {
    errors.totalSyndicationAmount = 'Syndication amount must be a positive number.';
  }
  const retained = Number(terms.leadRetainedAmount);
  if (terms.leadRetainedAmount === '' || !Number.isFinite(retained) || retained < 0) {
    errors.leadRetainedAmount = 'Retained amount is required (0 or more).';
  }
  if (
    !errors.loanAmount && !errors.totalSyndicationAmount && !errors.leadRetainedAmount
    && total + retained > loanAmount
  ) {
    errors.totalSyndicationAmount =
      'Syndication amount plus retained amount must not exceed the loan amount.';
  }
  const minimum = Number(terms.minimumCommitment);
  if (!terms.minimumCommitment || !Number.isFinite(minimum) || minimum <= 0) {
    errors.minimumCommitment = 'Minimum commitment must be a positive number.';
  } else if (!errors.totalSyndicationAmount && minimum > total) {
    errors.minimumCommitment = 'Minimum commitment must not exceed the syndication amount.';
  }
  if (terms.maximumCommitment !== '') {
    const maximum = Number(terms.maximumCommitment);
    if (!Number.isFinite(maximum) || maximum <= 0) {
      errors.maximumCommitment = 'Maximum commitment must be a positive number when set.';
    } else if (!errors.minimumCommitment && maximum < minimum) {
      errors.maximumCommitment = 'Maximum commitment must be at least the minimum commitment.';
    }
  }
  const participants = Number(terms.maximumParticipants);
  if (!terms.maximumParticipants || !Number.isInteger(participants) || participants < 1) {
    errors.maximumParticipants = 'Maximum participants must be a whole number, at least 1.';
  }
  if (terms.servicingFeeRate !== '') {
    const fee = Number(terms.servicingFeeRate);
    if (!Number.isFinite(fee) || fee < 0) {
      errors.servicingFeeRate = 'Servicing fee rate must be 0 or more when set.';
    }
  }
  return errors;
}

export function validateAmort(amort: AmortFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!amort.amortType) {
    if (amort.prepayModel || amort.prepaySpeed !== '') {
      errors.prepayModel = 'Prepayment inputs require an amortization type.';
    }
    return errors;
  }
  if (amort.prepayModel === 'CPR' || amort.prepayModel === 'PSA') {
    const speed = Number(amort.prepaySpeed);
    if (amort.prepaySpeed === '' || !Number.isFinite(speed)) {
      errors.prepaySpeed = 'Prepayment speed is required for CPR/PSA.';
    } else if (amort.prepayModel === 'CPR' && (speed < 0 || speed > 1)) {
      errors.prepaySpeed = 'CPR is a decimal fraction in [0, 1] (0.08 = 8% CPR).';
    } else if (amort.prepayModel === 'PSA' && speed < 0) {
      errors.prepaySpeed = 'PSA is a percent, 0 or more (150 = 150 PSA).';
    }
  } else if (amort.prepaySpeed !== '') {
    errors.prepaySpeed = 'Prepayment speed applies only to CPR/PSA models.';
  }
  if (amort.amortType === 'CUSTOM_SCHEDULE') {
    if (amort.scheduleRows.length === 0) {
      errors.scheduleRows = 'A custom schedule requires at least one row.';
    } else {
      for (const row of amort.scheduleRows) {
        if (!row.date || row.scheduledPrincipal === '' || row.balance === ''
          || !Number.isFinite(Number(row.scheduledPrincipal))
          || !Number.isFinite(Number(row.balance))) {
          errors.scheduleRows = 'Every schedule row needs a date, scheduled principal, and balance.';
          break;
        }
      }
    }
  } else if (amort.scheduleRows.length > 0) {
    errors.scheduleRows = 'Explicit rows are only allowed for a custom schedule.';
  }
  return errors;
}

export function validateVisibility(visibility: VisibilityFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (visibility.networkVisibility === 'PRIVATE_INVITE') {
    const ids = visibility.invitedTenantIds.map((id) => id.trim()).filter((id) => id !== '');
    if (ids.length === 0) {
      errors.invitedTenantIds = 'A private listing requires at least one invited institution id.';
    } else if (ids.some((id) => !UUID_SHAPE.test(id))) {
      errors.invitedTenantIds = 'Every invited institution id must be a UUID.';
    }
  }
  return errors;
}

// ---------------------------------------------------------------------------
// Payload assembly -- typed against the generated CreateOfferingRequest
// ---------------------------------------------------------------------------

export function buildCreateOfferingRequest(args: {
  leadParticipantId: string;
  loan: LoanViewItem;
  terms: TermsFormState;
  amort: AmortFormState;
  visibility: VisibilityFormState;
}): CreateOfferingRequest {
  const { leadParticipantId, loan, terms, amort, visibility } = args;

  const loanDetails: LoanDetails = {
    loanAmount: Number(terms.loanAmount),
    interestRate: Number(terms.interestRate),
    rateType: terms.rateType,
    termMonths: Number(terms.termMonths),
    loanType: terms.loanType as LoanDetails['loanType'],
    originationDate: terms.originationDate,
    paymentFrequency: terms.paymentFrequency,
    ...(terms.maturityDate ? { maturityDate: terms.maturityDate } : {}),
    ...(terms.servicingFeeRate !== ''
      ? { servicingFeeRate: Number(terms.servicingFeeRate) }
      : {}),
  };

  const request: CreateOfferingRequest = {
    leadParticipantId,
    loanDetails,
    collateralEvaluationId: terms.collateralEvaluationId.trim(),
    participationStructure: {
      totalSyndicationAmount: Number(terms.totalSyndicationAmount),
      leadRetainedAmount: Number(terms.leadRetainedAmount),
      minimumCommitment: Number(terms.minimumCommitment),
      ...(terms.maximumCommitment !== ''
        ? { maximumCommitment: Number(terms.maximumCommitment) }
        : {}),
      maximumParticipants: Number(terms.maximumParticipants),
      distributionWaterfall: terms.distributionWaterfall,
    },
    // The date the lead picked closes out end-of-day UTC; the field label
    // states this convention on screen.
    commitmentDeadline: `${terms.commitmentDeadline}T23:59:59.000Z`,
    // GH #2162: the ASC 860 attestation is a persisted fact on the offering
    // -- the boolean always rides the payload (the UI's submit gate means it
    // is true on every real submit; the helper stays honest either way).
    trueSaleAttested: terms.trueSaleAttested,
    networkVisibility: visibility.networkVisibility,
    ...(terms.dealName.trim() ? { dealName: terms.dealName.trim() } : {}),
    ...(loan.loanId ? { loanId: loan.loanId } : {}),
    ...(visibility.networkVisibility === 'PRIVATE_INVITE'
      ? {
        invitedTenantIds: visibility.invitedTenantIds
          .map((id) => id.trim())
          .filter((id) => id !== ''),
      }
      : {}),
    ...(amort.amortType ? { amortType: amort.amortType } : {}),
    ...(amort.amortType && amort.prepayModel ? { prepayModel: amort.prepayModel } : {}),
    ...(amort.amortType && (amort.prepayModel === 'CPR' || amort.prepayModel === 'PSA')
      ? { prepaySpeed: Number(amort.prepaySpeed) }
      : {}),
    ...(amort.amortType === 'CUSTOM_SCHEDULE'
      ? {
        amortizationSchedule: amort.scheduleRows.map((row): AmortizationScheduleRow => ({
          date: row.date,
          scheduledPrincipal: Number(row.scheduledPrincipal),
          balance: Number(row.balance),
        })),
      }
      : {}),
  };
  return request;
}

// ---------------------------------------------------------------------------
// Backend error -> field mapping. The message always renders VERBATIM;
// this only decides WHERE (a specific field vs the submit banner).
// ---------------------------------------------------------------------------

export function mapCreateErrorToField(
  code: string | undefined,
  message: string,
): string | null {
  if (code === 'LOAN_NOT_FOUND') return 'loan';
  const normalized = message.toLowerCase();
  const rules: Array<[string, string]> = [
    ['invitedtenantids', 'invitedTenantIds'],
    ['collateralevaluationid', 'collateralEvaluationId'],
    ['commitmentdeadline', 'commitmentDeadline'],
    ['totalsyndicationamount', 'totalSyndicationAmount'],
    ['leadretainedamount', 'totalSyndicationAmount'],
    ['minimumcommitment', 'minimumCommitment'],
    ['maximumcommitment', 'maximumCommitment'],
    ['leadparticipantid', 'leadParticipantId'],
    ['prepay', 'amortization'],
    ['amort', 'amortization'],
    ['schedule', 'amortization'],
    ['termmonths', 'termMonths'],
    ['loanamount', 'loanAmount'],
    ['loanid', 'loan'],
  ];
  // A service message often names two fields ('minimumCommitment must not
  // exceed totalSyndicationAmount'); the SUBJECT of the sentence comes
  // first, so map to the earliest-occurring needle, not list order.
  let bestField: string | null = null;
  let bestIndex = Number.POSITIVE_INFINITY;
  for (const [needle, field] of rules) {
    const index = normalized.indexOf(needle);
    if (index !== -1 && index < bestIndex) {
      bestIndex = index;
      bestField = field;
    }
  }
  return bestField;
}

// ---------------------------------------------------------------------------
// Data hooks
// ---------------------------------------------------------------------------

function useCreateOffering() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateOfferingRequest) => {
      const { data, error } = await loanSyndicationClient.POST(
        '/api/v1/syndication/offerings',
        { body },
      );
      if (error) {
        const typed = error as Partial<ErrorResponse>;
        const err = new Error(typed.message ?? 'Offering creation failed');
        (err as Error & { code?: string }).code = typed.error;
        throw err;
      }
      return data as ParticipationOffering;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['lsp-marketplace'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Small presentation helpers
// ---------------------------------------------------------------------------

const STEP_LABELS = ['Select loan', 'Terms', 'Amortization', 'Visibility and review'] as const;

/**
 * The wizard's progress. The current step is carried by aria-current as well
 * as by its border and weight, which assistive technology does not announce
 * (r3 fi-g9-11).
 */
function StepIndicator({ current }: { current: number }) {
  return (
    <ol aria-label="Wizard progress" className="flex flex-wrap items-center gap-2 mb-6">
      {STEP_LABELS.map((label, index) => {
        const step = index + 1;
        const isCurrent = step === current;
        const isDone = step < current;
        return (
          <li
            key={label}
            aria-current={isCurrent ? 'step' : undefined}
            className="flex items-center gap-2"
          >
            <span
              className={`inline-flex items-center gap-1.5 rounded-sm border px-2.5 py-1 font-sans text-xs ${
                isCurrent
                  ? 'border-secondary bg-surface font-semibold text-primary'
                  : isDone
                    ? 'border-recessed text-secondary'
                    : 'border-recessed/60 text-tertiary'
              }`}
            >
              <span className="tabular-nums">{step}.</span>
              {label}
            </span>
            {step < STEP_LABELS.length && (
              <span aria-hidden className="font-sans text-xs text-tertiary">/</span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

const RAW_STATUS_BADGE: Record<string, 'success' | 'warning' | 'error' | 'neutral'> = {
  ACTIVE: 'success',
  FUNDED: 'success',
  APPROVED: 'warning',
  SUSPENDED: 'error',
  DEFAULTED: 'error',
};

// ---------------------------------------------------------------------------
// The wizard page
// ---------------------------------------------------------------------------

export interface ListALoanInitialState {
  step?: 1 | 2 | 3 | 4;
  loan?: LoanViewItem;
  terms?: TermsFormState;
  amort?: AmortFormState;
  visibility?: VisibilityFormState;
}

/**
 * `initialState` exists for component tests: it seeds a completed draft so
 * the review/submit step can be exercised without scripting every
 * keystroke of the four steps. Production routes render the page with no
 * props (always starts at step 1, empty).
 */
export function ListALoan({ initialState }: { initialState?: ListALoanInitialState } = {}) {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3 | 4>(initialState?.step ?? 1);
  const [search, setSearch] = useState('');
  const [selectedLoan, setSelectedLoan] = useState<LoanViewItem | null>(initialState?.loan ?? null);
  const [terms, setTerms] = useState<TermsFormState>(initialState?.terms ?? EMPTY_TERMS);
  const [amort, setAmort] = useState<AmortFormState>(initialState?.amort ?? EMPTY_AMORT);
  const [visibility, setVisibility] = useState<VisibilityFormState>(
    initialState?.visibility ?? EMPTY_VISIBILITY,
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [selectedParticipantId, setSelectedParticipantId] = useState('');

  const loansQuery = useActiveLoans({ status: 'Active', search: search || undefined, page: 1, limit: 50 });
  const participantsQuery = useOwnParticipants();
  const creation = useCreateOffering();

  const participants = participantsQuery.data?.participants ?? [];
  const leadParticipantId = participants.length === 1
    ? participants[0].participantId
    : selectedParticipantId;

  const loans = loansQuery.data?.data ?? [];

  function setTermField<K extends keyof TermsFormState>(key: K, value: TermsFormState[K]) {
    setTerms((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key as string];
      return next;
    });
  }

  const pickLoan = useCallback((loan: LoanViewItem) => {
    setSelectedLoan(loan);
    // Re-derive the prefill for the newly picked loan; the lead's edits on
    // a previously picked loan do not bleed into this one.
    setTerms({ ...EMPTY_TERMS, ...prefillFromLoan(loan) });
    setFieldErrors({});
    setStep(2);
  }, []);

  function continueFromTerms() {
    const errors = validateTerms(terms);
    setFieldErrors(errors);
    if (Object.keys(errors).length === 0) setStep(3);
  }

  function continueFromAmort() {
    const errors = validateAmort(amort);
    setFieldErrors(errors);
    if (Object.keys(errors).length === 0) setStep(4);
  }

  const visibilityErrors = validateVisibility(visibility);
  const canSubmit =
    !!selectedLoan
    && !!leadParticipantId
    && terms.trueSaleAttested
    && Object.keys(validateTerms(terms)).length === 0
    && Object.keys(validateAmort(amort)).length === 0
    && Object.keys(visibilityErrors).length === 0
    && !creation.isPending;

  function handleSubmit() {
    if (!selectedLoan || !leadParticipantId) return;
    const errors = {
      ...validateTerms(terms),
      ...validateAmort(amort),
      ...validateVisibility(visibility),
    };
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    const body = buildCreateOfferingRequest({
      leadParticipantId,
      loan: selectedLoan,
      terms,
      amort,
      visibility,
    });
    creation.mutate(body, {
      onSuccess: (offering) => {
        navigate(`/syndication/offerings/${offering.offeringId}`);
      },
      onError: (error) => {
        const code = (error as Error & { code?: string }).code;
        const field = mapCreateErrorToField(code, error.message);
        if (field) {
          setFieldErrors((prev) => ({ ...prev, [field]: error.message }));
        }
      },
    });
  }

  const serverErrorCode = creation.isError
    ? (creation.error as Error & { code?: string }).code
    : undefined;
  const serverErrorField = creation.isError
    ? mapCreateErrorToField(serverErrorCode, creation.error.message)
    : null;

  // -------------------------------------------------------------------------
  // Step 1: pick the loan
  // -------------------------------------------------------------------------

  const loanColumns = useMemo<ColumnDef<LoanViewItem, unknown>[]>(
    () => [
      {
        id: 'loanNumber',
        header: 'Loan',
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate font-sans text-sm font-medium text-primary leading-normal">
              {row.original.loanNumber ?? row.original.id}
            </div>
            <div className="truncate font-sans text-xs text-tertiary leading-normal">
              {/* A loan type code ('SECURITIES_BACKED') prints through the
                  shared label map; free-text collateral prints as written. */}
              {labelFor(LOAN_TYPE_LABELS, row.original.collateralType)}
            </div>
          </div>
        ),
        size: 220,
      },
      {
        id: 'loanStatus',
        header: 'Status',
        cell: ({ row }) => {
          const raw = row.original.loanStatus;
          if (!raw) return <span className="font-sans text-xs text-tertiary">--</span>;
          return (
            <Badge variant={RAW_STATUS_BADGE[raw] ?? 'neutral'}>
              {loanStatusLabel(raw)}
            </Badge>
          );
        },
        size: 110,
      },
      {
        id: 'outstanding',
        header: 'Outstanding',
        meta: { align: 'right' },
        cell: ({ row }) => (
          <div className="text-right font-sans text-sm text-primary tabular-nums">
            {formatCurrencyGrid(row.original.outstandingBalance)}
          </div>
        ),
        size: 110,
      },
      {
        id: 'principal',
        header: 'Principal',
        meta: { align: 'right' },
        cell: ({ row }) => (
          <div className="text-right font-sans text-sm text-secondary tabular-nums">
            {formatCurrencyGrid(row.original.loanAmount)}
          </div>
        ),
        size: 110,
      },
      {
        id: 'rate',
        header: 'Rate',
        meta: { align: 'right' },
        cell: ({ row }) => (
          <div className="text-right font-sans text-sm text-primary tabular-nums">
            {formatPercent(row.original.interestRate)}
          </div>
        ),
        size: 84,
      },
      {
        id: 'maturity',
        header: 'Maturity',
        meta: { align: 'right' },
        cell: ({ row }) => (
          <div className="text-right font-sans text-sm text-secondary tabular-nums">
            {formatDate(row.original.maturityDate)}
          </div>
        ),
        size: 110,
      },
      {
        id: 'action',
        header: '',
        cell: ({ row }) => {
          const syndicable = isSyndicableStatus(row.original.loanStatus);
          if (!syndicable) {
            return (
              <span className="font-sans text-xs text-tertiary">
                {NON_SYNDICABLE_REASON[row.original.loanStatus ?? ''] ?? 'Not listable.'}
              </span>
            );
          }
          return (
            <Button variant="tertiary" size="sm" onClick={() => pickLoan(row.original)}>
              Select
            </Button>
          );
        },
        size: 200,
      },
    ],
    [pickLoan],
  );

  function renderStep1() {
    if (loansQuery.isLoading) {
      return (
        <Card className="p-6">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full mb-2" />
          ))}
        </Card>
      );
    }
    if (loansQuery.error) {
      // The loans read (enableLosPreview surface) 404s by design when the
      // flag is off for this tenant. Render the honest gate, not a crash.
      return (
        <Card>
          <EmptyState
            icon={<Inbox className="h-8 w-8" />}
            title="Loan book unavailable"
            description="The originated-loan read for your institution is not enabled (it is served by the LOS-preview surface). Listing a participation starts from a loan record, so this wizard needs that read."
          />
          <div className="px-6 pb-6 text-center font-sans text-xs text-secondary">
            <Link
              to={requestChangeHref({
                surface: 'Syndication -- list a loan',
                item: 'Originated-loan read access (enableLosPreview)',
                detail: 'The list-a-loan wizard cannot read my institution’s loan book',
              })}
              className="text-accent hover:underline"
            >
              Request loan book access
            </Link>
          </div>
        </Card>
      );
    }
    return (
      <>
        <div className="mb-4 max-w-md">
          <Input
            label="Search loans"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Loan number or borrower name..."
          />
        </div>
        {loans.length === 0 ? (
          <Card>
            <EmptyState
              icon={<Inbox className="h-8 w-8" />}
              title="No loans found"
              description={
                search
                  ? 'No loans match the search. Clear it to see the full book.'
                  : 'No active or funded loans on your institution’s book.'
              }
            />
          </Card>
        ) : (
          // The fixed layout sizes columns as percentages of the container,
          // so at phone width seven columns squeezed into 358px and their
          // headers overprinted (fi-g9-6). minTableWidth keeps the grid
          // legible and lets it scroll inside its own overflow container.
          <DataGrid<LoanViewItem>
            data={loans}
            columns={loanColumns}
            enablePagination
            defaultPageSize={10}
            fixedLayout
            minTableWidth={720}
            hoverable
            density="compact"
            getRowId={(row) => row.loanId ?? row.id}
            className="w-full rounded-sm shadow-sm"
          />
        )}
        {fieldErrors.loan && (
          <div className="mt-4 bg-error/5 border border-error/20 rounded-sm p-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-error flex-shrink-0" />
            <div className="font-sans text-xs text-error">{fieldErrors.loan}</div>
          </div>
        )}
      </>
    );
  }

  // -------------------------------------------------------------------------
  // Step 2: terms
  // -------------------------------------------------------------------------

  const retainedPctDisplay = useMemo(() => {
    const loanAmount = Number(terms.loanAmount);
    const retained = Number(terms.leadRetainedAmount);
    if (!Number.isFinite(loanAmount) || loanAmount <= 0 || !Number.isFinite(retained) || retained < 0) {
      return null;
    }
    return retained / loanAmount;
  }, [terms.loanAmount, terms.leadRetainedAmount]);

  function renderStep2() {
    if (!selectedLoan) return null;
    return (
      <div className="flex flex-col gap-6">
        <div className="font-sans text-xs text-secondary">
          Listing from loan{' '}
          <span className="font-medium text-primary">
            {selectedLoan.loanNumber ?? selectedLoan.id}
          </span>
          . Fields carried by the loan record are prefilled and stay editable;
          everything else is yours to supply -- nothing is assumed.
        </div>

        <Card className="p-6">
          <div className="mb-3 font-sans text-xs font-semibold uppercase tracking-wide text-tertiary">
            Listing
          </div>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Input
                label="Deal name (public descriptor)"
                type="text"
                value={terms.dealName}
                onChange={(e) => setTermField('dealName', e.target.value)}
                maxLength={255}
                error={fieldErrors.dealName}
              />
              <FormHelperText>
                Suggested from the loan record; edit freely. The descriptor is
                public marketplace data and is never masked -- if you lead
                anonymously, pick a name that does not identify your
                institution or the borrower.
              </FormHelperText>
            </div>
            <div>
              <Input
                label="Collateral evaluation id"
                type="text"
                value={terms.collateralEvaluationId}
                onChange={(e) => setTermField('collateralEvaluationId', e.target.value)}
                placeholder="UUID of the active Aaim collateral evaluation"
                error={fieldErrors.collateralEvaluationId}
              />
              <FormHelperText>
                The active Aaim collateral evaluation for the pledged assets
                backing this loan. The loan record does not carry it; it comes
                from the evaluation you ran on the collateral package.
              </FormHelperText>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="mb-3 font-sans text-xs font-semibold uppercase tracking-wide text-tertiary">
            Loan terms
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Loan amount (USD, originated total)"
              type="number"
              value={terms.loanAmount}
              onChange={(e) => setTermField('loanAmount', e.target.value)}
              min={0}
              error={fieldErrors.loanAmount}
            />
            <div>
              <Input
                label="Coupon rate (decimal fraction)"
                type="number"
                value={terms.interestRate}
                onChange={(e) => setTermField('interestRate', e.target.value)}
                min={0}
                step="0.0001"
                error={fieldErrors.interestRate}
              />
              <FormHelperText>0.065 = 6.5% -- same convention as the loan record.</FormHelperText>
            </div>
            <Select
              label="Rate type"
              value={terms.rateType}
              onChange={(e) => setTermField('rateType', e.target.value as TermsFormState['rateType'])}
              options={[
                { value: 'FIXED', label: 'Fixed' },
                { value: 'VARIABLE', label: 'Variable' },
              ]}
            />
            <Input
              label="Term (months)"
              type="number"
              value={terms.termMonths}
              onChange={(e) => setTermField('termMonths', e.target.value)}
              min={1}
              step={1}
              error={fieldErrors.termMonths}
            />
            <div>
              <Select
                label="Loan type"
                value={terms.loanType}
                onChange={(e) => setTermField('loanType', e.target.value as TermsFormState['loanType'])}
                placeholder="Select loan type"
                options={[
                  { value: 'PLEDGED_ASSET', label: 'Pledged asset' },
                  { value: 'SECURITIES_BACKED', label: 'Securities-backed' },
                  { value: 'DIGITAL_ASSET_BACKED', label: 'Digital-asset-backed' },
                ]}
                error={fieldErrors.loanType}
              />
              <FormHelperText>
                The loan record's own type vocabulary does not map onto this
                enum, so it is not prefilled -- pick the offering
                classification yourself.
              </FormHelperText>
            </div>
            <Input
              label="Origination date"
              type="date"
              value={terms.originationDate}
              onChange={(e) => setTermField('originationDate', e.target.value)}
              error={fieldErrors.originationDate}
            />
            <Input
              label="Maturity date (optional)"
              type="date"
              value={terms.maturityDate}
              onChange={(e) => setTermField('maturityDate', e.target.value)}
              error={fieldErrors.maturityDate}
            />
            <Select
              label="Payment frequency"
              value={terms.paymentFrequency}
              onChange={(e) =>
                setTermField('paymentFrequency', e.target.value as TermsFormState['paymentFrequency'])}
              options={[
                { value: 'MONTHLY', label: 'Monthly' },
                { value: 'QUARTERLY', label: 'Quarterly' },
              ]}
            />
            <div>
              <Input
                label="Servicing fee rate (optional, decimal fraction)"
                type="number"
                value={terms.servicingFeeRate}
                onChange={(e) => setTermField('servicingFeeRate', e.target.value)}
                min={0}
                step="0.0001"
                error={fieldErrors.servicingFeeRate}
              />
              <FormHelperText>0.0025 = 25 bps retained by the lead for servicing.</FormHelperText>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="mb-3 font-sans text-xs font-semibold uppercase tracking-wide text-tertiary">
            Participation structure
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Total syndication amount (USD)"
              type="number"
              value={terms.totalSyndicationAmount}
              onChange={(e) => setTermField('totalSyndicationAmount', e.target.value)}
              min={0}
              error={fieldErrors.totalSyndicationAmount}
            />
            <div>
              <Input
                label="Lead retained amount (USD)"
                type="number"
                value={terms.leadRetainedAmount}
                onChange={(e) => setTermField('leadRetainedAmount', e.target.value)}
                min={0}
                error={fieldErrors.leadRetainedAmount}
              />
              {retainedPctDisplay !== null && (
                <FormHelperText>
                  {formatPercent(retainedPctDisplay)} of the loan amount stays
                  on your balance sheet.
                </FormHelperText>
              )}
            </div>
            <Input
              label="Minimum commitment (USD)"
              type="number"
              value={terms.minimumCommitment}
              onChange={(e) => setTermField('minimumCommitment', e.target.value)}
              min={0}
              error={fieldErrors.minimumCommitment}
            />
            <Input
              label="Maximum commitment (USD, optional)"
              type="number"
              value={terms.maximumCommitment}
              onChange={(e) => setTermField('maximumCommitment', e.target.value)}
              min={0}
              error={fieldErrors.maximumCommitment}
            />
            <Input
              label="Maximum participants"
              type="number"
              value={terms.maximumParticipants}
              onChange={(e) => setTermField('maximumParticipants', e.target.value)}
              min={1}
              step={1}
              error={fieldErrors.maximumParticipants}
            />
            <Select
              label="Distribution waterfall"
              value={terms.distributionWaterfall}
              onChange={(e) =>
                setTermField(
                  'distributionWaterfall',
                  e.target.value as TermsFormState['distributionWaterfall'],
                )}
              options={[
                { value: 'PRO_RATA', label: 'Pro rata' },
                { value: 'SEQUENTIAL', label: 'Sequential' },
              ]}
            />
            <Input
              label="Commitment deadline (closes end of day, UTC)"
              type="date"
              value={terms.commitmentDeadline}
              onChange={(e) => setTermField('commitmentDeadline', e.target.value)}
              error={fieldErrors.commitmentDeadline}
            />
          </div>
        </Card>

        <Card className="p-6">
          <div className="mb-3 font-sans text-xs font-semibold uppercase tracking-wide text-tertiary">
            True-sale attestation (ASC 860)
          </div>
          <p className="mb-3 font-sans text-xs text-secondary">
            A participation sold under this listing is an asset sale, not a
            secured borrowing. Participants take the obligor's credit risk;
            the originator retains no obligation to repurchase for credit
            deterioration -- repurchase stays limited to origination-defect
            grounds (representation or warranty breach, early payment
            default, fraud). Repurchase for credit deterioration would
            recharacterize the transaction as a financing and collapse the
            sale treatment under ASC 860. Your attestation is recorded on
            the offering, with who attested and when.
          </p>
          <Checkbox
            label="I attest this participation is structured as a true sale under ASC 860"
            checked={terms.trueSaleAttested}
            onChange={(checked) => setTermField('trueSaleAttested', checked)}
          />
        </Card>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Step 3: amortization (optional)
  // -------------------------------------------------------------------------

  function renderStep3() {
    return (
      <div className="flex flex-col gap-6">
        <div className="font-sans text-xs text-secondary">
          Optional. With an amortization structure, buyers get a cash-flow
          projection (yield-to-maturity, weighted average life) on the
          listing; without one, the offering carries no amortization data and
          those figures are omitted -- never fabricated.
        </div>
        <Card className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              label="Amortization type"
              value={amort.amortType}
              onChange={(e) => {
                const value = e.target.value as AmortFormState['amortType'];
                setAmort((prev) => ({
                  ...prev,
                  amortType: value,
                  ...(value === '' ? { prepayModel: '', prepaySpeed: '', scheduleRows: [] } : {}),
                  ...(value !== 'CUSTOM_SCHEDULE' ? { scheduleRows: [] } : {}),
                }));
                setFieldErrors({});
              }}
              placeholder="None (no amortization data)"
              options={[
                { value: 'BULLET', label: 'Bullet' },
                { value: 'LEVEL_PAYMENT', label: 'Level payment' },
                { value: 'LEVEL_PRINCIPAL', label: 'Level principal' },
                { value: 'CUSTOM_SCHEDULE', label: 'Custom schedule' },
              ]}
            />
            {amort.amortType && (
              <Select
                label="Prepayment model"
                value={amort.prepayModel}
                onChange={(e) => {
                  const value = e.target.value as AmortFormState['prepayModel'];
                  setAmort((prev) => ({
                    ...prev,
                    prepayModel: value,
                    ...(value !== 'CPR' && value !== 'PSA' ? { prepaySpeed: '' } : {}),
                  }));
                  setFieldErrors({});
                }}
                placeholder="None"
                options={[
                  { value: 'NONE', label: 'None' },
                  { value: 'CPR', label: 'CPR (constant prepayment rate)' },
                  { value: 'PSA', label: 'PSA (standard prepayment assumption)' },
                ]}
                error={fieldErrors.prepayModel}
              />
            )}
            {(amort.prepayModel === 'CPR' || amort.prepayModel === 'PSA') && (
              <div>
                <Input
                  label="Prepayment speed"
                  type="number"
                  value={amort.prepaySpeed}
                  onChange={(e) => {
                    const value = e.target.value;
                    setAmort((prev) => ({ ...prev, prepaySpeed: value }));
                    setFieldErrors({});
                  }}
                  min={0}
                  step="0.01"
                  error={fieldErrors.prepaySpeed}
                />
                <FormHelperText>
                  {amort.prepayModel === 'CPR'
                    ? 'CPR is the annual rate as a decimal fraction in [0, 1]: 0.08 = 8% CPR, the same decimal convention as the coupon rate.'
                    : 'PSA is a percent, 0 or more: 150 = 150 PSA.'}
                </FormHelperText>
              </div>
            )}
          </div>

          {amort.amortType === 'CUSTOM_SCHEDULE' && (
            <div className="mt-6">
              <div className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-tertiary">
                Schedule rows
              </div>
              <p className="mb-3 font-sans text-xs text-secondary">
                The rows define the period grid and must amortize the loan to
                exactly zero; the server cross-validates the projection at
                create time and refuses an incoherent schedule.
              </p>
              {amort.scheduleRows.map((row, index) => (
                <div key={index} className="mb-2 flex flex-wrap items-end gap-2">
                  <Input
                    label={index === 0 ? 'Date' : undefined}
                    type="date"
                    value={row.date}
                    onChange={(e) => {
                      const value = e.target.value;
                      setAmort((prev) => ({
                        ...prev,
                        scheduleRows: prev.scheduleRows.map((r, i) =>
                          i === index ? { ...r, date: value } : r),
                      }));
                    }}
                  />
                  <Input
                    label={index === 0 ? 'Scheduled principal' : undefined}
                    type="number"
                    value={row.scheduledPrincipal}
                    onChange={(e) => {
                      const value = e.target.value;
                      setAmort((prev) => ({
                        ...prev,
                        scheduleRows: prev.scheduleRows.map((r, i) =>
                          i === index ? { ...r, scheduledPrincipal: value } : r),
                      }));
                    }}
                    min={0}
                  />
                  <Input
                    label={index === 0 ? 'Balance after' : undefined}
                    type="number"
                    value={row.balance}
                    onChange={(e) => {
                      const value = e.target.value;
                      setAmort((prev) => ({
                        ...prev,
                        scheduleRows: prev.scheduleRows.map((r, i) =>
                          i === index ? { ...r, balance: value } : r),
                      }));
                    }}
                    min={0}
                  />
                  <Button
                    variant="tertiary"
                    size="sm"
                    icon={Trash2}
                    aria-label={`Remove schedule row ${index + 1}`}
                    onClick={() =>
                      setAmort((prev) => ({
                        ...prev,
                        scheduleRows: prev.scheduleRows.filter((_, i) => i !== index),
                      }))}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                variant="secondary"
                size="sm"
                icon={Plus}
                onClick={() =>
                  setAmort((prev) => ({
                    ...prev,
                    scheduleRows: [
                      ...prev.scheduleRows,
                      { date: '', scheduledPrincipal: '', balance: '' },
                    ],
                  }))}
              >
                Add row
              </Button>
              {fieldErrors.scheduleRows && (
                <div className="mt-2 font-sans text-xs text-error">{fieldErrors.scheduleRows}</div>
              )}
            </div>
          )}
        </Card>
        {fieldErrors.amortization && (
          <div className="bg-error/5 border border-error/20 rounded-sm p-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-error flex-shrink-0" />
            <div className="font-sans text-xs text-error">{fieldErrors.amortization}</div>
          </div>
        )}
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Step 4: visibility + review + submit
  // -------------------------------------------------------------------------

  function renderStep4() {
    const reviewRows: Array<[string, string]> = [
      ['Loan', selectedLoan ? (selectedLoan.loanNumber ?? selectedLoan.id) : '--'],
      ['Deal name', terms.dealName || '--'],
      ['Loan amount', formatCurrencyGrid(Number(terms.loanAmount) || undefined)],
      ['Coupon', terms.interestRate ? formatPercent(Number(terms.interestRate)) : '--'],
      ['Term', terms.termMonths ? `${terms.termMonths} months` : '--'],
      ['Syndication amount', formatCurrencyGrid(Number(terms.totalSyndicationAmount) || undefined)],
      ['Lead retained', formatCurrencyGrid(Number(terms.leadRetainedAmount) || undefined)],
      ['Minimum commitment', formatCurrencyGrid(Number(terms.minimumCommitment) || undefined)],
      ['Max participants', terms.maximumParticipants || '--'],
      ['Deadline', terms.commitmentDeadline ? `${terms.commitmentDeadline} (end of day UTC)` : '--'],
      ['Amortization', amort.amortType || 'None'],
      ['Visibility', visibility.networkVisibility === 'NETWORK' ? 'Network' : 'Private invite'],
      ['True-sale attested', terms.trueSaleAttested ? 'Yes' : 'No'],
    ];

    return (
      <div className="flex flex-col gap-6">
        <Card className="p-6">
          <div className="mb-3 font-sans text-xs font-semibold uppercase tracking-wide text-tertiary">
            Visibility
          </div>
          <Select
            label="Listing visibility"
            value={visibility.networkVisibility}
            onChange={(e) => {
              const value = e.target.value as NetworkVisibility;
              setVisibility((prev) => ({
                ...prev,
                networkVisibility: value,
                ...(value === 'NETWORK' ? { invitedTenantIds: [] } : {}),
              }));
              setFieldErrors((prev) => {
                const next = { ...prev };
                delete next.invitedTenantIds;
                return next;
              });
            }}
            options={[
              { value: 'NETWORK', label: 'Network -- visible to all eligible institutions' },
              { value: 'PRIVATE_INVITE', label: 'Private invite -- visible only to invited institutions' },
            ]}
          />
          {visibility.networkVisibility === 'PRIVATE_INVITE' && (
            <div className="mt-4">
              <div className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-tertiary">
                Invited institution ids
              </div>
              <p className="mb-3 font-sans text-xs text-secondary">
                The invite set is your consent boundary: the listing appears
                only for these institutions (plus your own institution and
                Aaim operators). At least one institution id is required; the
                set is managed after create via the invite list.
              </p>
              {visibility.invitedTenantIds.map((id, index) => (
                <div key={index} className="mb-2 flex items-end gap-2">
                  <div className="flex-1">
                    <Input
                      label={index === 0 ? 'Institution id (UUID)' : undefined}
                      type="text"
                      value={id}
                      onChange={(e) => {
                        const value = e.target.value;
                        setVisibility((prev) => ({
                          ...prev,
                          invitedTenantIds: prev.invitedTenantIds.map((t, i) =>
                            i === index ? value : t),
                        }));
                      }}
                    />
                  </div>
                  <Button
                    variant="tertiary"
                    size="sm"
                    icon={Trash2}
                    aria-label={`Remove invited institution ${index + 1}`}
                    onClick={() =>
                      setVisibility((prev) => ({
                        ...prev,
                        invitedTenantIds: prev.invitedTenantIds.filter((_, i) => i !== index),
                      }))}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                variant="secondary"
                size="sm"
                icon={Plus}
                onClick={() =>
                  setVisibility((prev) => ({
                    ...prev,
                    invitedTenantIds: [...prev.invitedTenantIds, ''],
                  }))}
              >
                Add institution
              </Button>
              {(fieldErrors.invitedTenantIds ?? visibilityErrors.invitedTenantIds) && (
                <div className="mt-2 font-sans text-xs text-error">
                  {fieldErrors.invitedTenantIds ?? visibilityErrors.invitedTenantIds}
                </div>
              )}
            </div>
          )}
        </Card>

        <Card className="p-6">
          <div className="mb-3 font-sans text-xs font-semibold uppercase tracking-wide text-tertiary">
            Review
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
            {reviewRows.map(([key, value]) => (
              <div
                key={key}
                className="flex items-baseline justify-between gap-3 border-b border-recessed/60 pb-1"
              >
                <dt className="font-sans text-xs text-tertiary">{key}</dt>
                <dd className="font-sans text-xs font-medium text-primary tabular-nums text-right">
                  {value}
                </dd>
              </div>
            ))}
          </dl>
          {!terms.trueSaleAttested && (
            <div className="mt-4 bg-warning/10 border border-warning/30 rounded-sm p-3 font-sans text-xs text-secondary">
              The true-sale attestation on the Terms step is required before
              this offering can be listed.
            </div>
          )}
        </Card>

        {participantsQuery.isError && (
          <div className="bg-error/5 border border-error/20 rounded-sm p-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-error flex-shrink-0" />
            <div className="font-sans text-xs text-error">
              {participantsQuery.error instanceof Error
                ? participantsQuery.error.message
                : 'Failed to load your network participant registration'}
            </div>
          </div>
        )}
        {!participantsQuery.isLoading && !participantsQuery.isError && participants.length === 0 && (
          <div className="bg-warning/10 border border-warning/30 rounded-sm p-3 font-sans text-xs text-secondary">
            Creating an offering requires an active network participant
            registration for your institution, and none exists.{' '}
            <Link
              to={requestChangeHref({
                surface: 'Syndication -- list a loan',
                item: 'Network participant registration for offering creation',
                detail: 'Offering creation is blocked: my institution has no active network participant registration',
              })}
              className="text-accent hover:underline"
            >
              Request participant activation
            </Link>
          </div>
        )}
        {participants.length > 1 && (
          <Select
            label="Lead participant registration"
            value={selectedParticipantId}
            onChange={(e) => setSelectedParticipantId(e.target.value)}
            placeholder="Select the registration to list under"
            options={participants.map((p) => ({
              value: p.participantId,
              label: `${p.institutionName} (${p.participantId.slice(0, 8)})`,
            }))}
            error={fieldErrors.leadParticipantId}
          />
        )}

        {creation.isError && !serverErrorField && (
          <div className="bg-error/5 border border-error/20 rounded-sm p-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-error flex-shrink-0" />
            <div className="font-sans text-xs text-error">
              {creation.error instanceof Error ? creation.error.message : 'Offering creation failed'}
            </div>
          </div>
        )}
        {creation.isError && serverErrorField && (
          <div className="bg-error/5 border border-error/20 rounded-sm p-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-error flex-shrink-0" />
            <div className="font-sans text-xs text-error">
              {creation.error.message}
              {serverErrorField === 'loan' && ' -- re-check the selected loan on step 1.'}
              {serverErrorField === 'amortization' && ' -- re-check the amortization inputs on step 3.'}
            </div>
          </div>
        )}
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Frame
  // -------------------------------------------------------------------------

  return (
    <div>
      {/* Page title lives in the sticky topnav (AppHeader); the body opens
          with the descriptive subtitle, no redundant H1 -- the pattern the
          rest of the Loan Participations group uses (fi-g9-9). */}
      <p className="mb-6 text-sm font-sans text-tertiary">
        Create a participation offering from one of your originated loans.
        Prefilled from the loan record where a real field exists; everything
        else is yours to set.
      </p>
      <StepIndicator current={step} />

      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}

      {/* Step 1 has no Back and no Continue (a row's Select advances), so the
          footer rule rendered alone under the loan grid (r3 fi-g9-10). */}
      {step > 1 && (
        <div className="mt-6 flex items-center justify-between gap-3 border-t border-recessed pt-4">
          <div>
            <Button
              variant="secondary"
              onClick={() => setStep((prev) => (prev - 1) as 1 | 2 | 3)}
              disabled={creation.isPending}
            >
              Back
            </Button>
          </div>
          <div className="flex items-center gap-2">
            {step === 2 && (
              <Button variant="primary" onClick={continueFromTerms}>
                Continue
              </Button>
            )}
            {step === 3 && (
              <Button variant="primary" onClick={continueFromAmort}>
                Continue
              </Button>
            )}
            {step === 4 && (
              <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
                {creation.isPending ? 'Listing...' : 'List offering'}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
